import React, { useEffect, useState } from "react";
import { useLazyFetchFlightsRedirectQuery } from "redux/services/flightSearch";
import { useSelector } from "react-redux";
import { UseAppDispatch } from "redux/store";
import useSetQueryParams from "hooks/useSetQueryParams";

import {
  setSearchForm,
  IFlight,
  ISearchPassengers,
  searchFormSelector,
} from "redux/searchForm";
import { updateSearchQuery } from "redux/searchQuery";
import useQueryParams, { IQueryParams } from "hooks/useQueryParams";
import {
  setRevalidateResult,
  setSelectedFareId,
} from "redux/selectedItinerary";
import { useTranslations } from "hooks/useTranslations";
import { backgroundAnimations } from "./animations.searchResultsPage";

import { resetAdditionalProducts } from "redux/additionalProducts";
import { resetShoppingCart } from "redux/shoppingCart";
import { resetCheckoutSteps, setShouldRevalidate } from "redux/checkoutSteps";
import { resetContactInformation } from "redux/contactInformation";
import {
  resetTravelerInformation,
  setTravelersList,
} from "redux/travelerInformation";

import {
  CustomAnimation,
  dateStringToDateObjNoTime,
  CtaDialog,
  formatDateShort,
  SlotMachineLoader,
  useConfig,
} from "@thekilroygroup/shared-ui";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";

import { getCityName } from "utils/flightDetailApiUtils";
import { subtitleAnimations, titleAnimations } from "utils/sharedAnimations";
import { searchFormAnimations } from "../FrontPage/animations.frontPage";
import { useNavigate } from "react-router-dom";
import { ResponseIconMap } from "components/ResponsePopup/ResponseIconMap";
import { buildQueryString } from "utils/queryStrings";
import { setupTravelers } from "components/Checkout/TravelerInformation/setupTravelers";
import { MetapartnerTracking } from "components/MetapartnerTracking/MetapartnerTracking";

const SearchRedirectPage = () => {
  const dispatch = UseAppDispatch();
  const params: IQueryParams = useQueryParams();
  const searchForm = useSelector(searchFormSelector);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  useSetQueryParams();

  const {
    translations: {
      search: { tripTypes },
      passengerTypesMap,
      customResponseCodes: {
        "4006": { CTA, title, subtitle, text, icon },
      },
    },
  } = useTranslations();
  const { locale, appConfig } = useConfig();

  const [trigger, result] = useLazyFetchFlightsRedirectQuery();
  const { data, isFetching, isError } = result;
  const theme = useTheme();
  const isTabletUp = useMediaQuery(theme.breakpoints.up("sm"));
  const navigate = useNavigate();

  useEffect(() => {
    // Reset all checkout steps
    dispatch(resetCheckoutSteps());
    dispatch(resetContactInformation());
    dispatch(resetTravelerInformation());
    dispatch(resetAdditionalProducts());
    dispatch(resetShoppingCart());

    // Set search form
    dispatch(setSearchForm({ ...params.searchQuery, isDirectBooking: true }));
    dispatch(setSelectedFareId(params.fareId));
    dispatch(
      updateSearchQuery({ ...params.searchQuery, isDirectBooking: true }),
    );

    trigger({
      searchForm: params.searchQuery,
      config: appConfig,
    });

    window.scrollTo({
      behavior: "smooth",
      top: 0,
    });
  }, []);

  useEffect(() => {
    if (isError && !isFetching) {
      setShowErrorMessage(true);
    } else if (data?.meta?.response.status === 204) {
      setShowErrorMessage(true);
    } else if (data?.apiResponse) {
      if (!isFetching && !isError) {
        dispatch(setRevalidateResult(data.apiResponse));
        dispatch(setShouldRevalidate(false));
        dispatch(
          setTravelersList(setupTravelers(data.apiResponse.itineraries[0])),
        );
        setTimeout(() => {
          navigate("/checkout");
        }, backgroundAnimations.delayOut);
      }
    }
  }, [data, isFetching, isError]);

  const getItineraryCities = (flights: IFlight[]) => {
    return `${getCityName(flights[0].departure || "")} - ${getCityName(
      flights[0].arrival || "",
    )}`;
  };

  const getTripType = (flights: IFlight[]) => {
    if (flights.length > 2) {
      return tripTypes["OPEN_JAW"];
    }
    if (flights.length === 2) {
      return tripTypes["RETURN"];
    }
    return tripTypes["ONE_WAY"];
  };

  const getTravelDates = (flights: IFlight[]) => {
    let dates = formatDateShort(
      dateStringToDateObjNoTime(flights[0].departureDate) as Date,
      locale,
    );
    if (flights.length > 1) {
      dates +=
        " - " +
        formatDateShort(
          dateStringToDateObjNoTime(
            flights[flights.length - 1].departureDate,
          ) as Date,
          locale,
        );
    }
    return dates;
  };

  const getTravelerTypes = (passengers: ISearchPassengers) => {
    const { adults, children, infants } = passengers;
    let travelerTypes = [];

    if (adults > 0) {
      travelerTypes.push(
        `${adults} ${
          adults > 1
            ? passengerTypesMap["ADT"].multiple
            : passengerTypesMap["ADT"].single
        }`,
      );
    }
    if (children > 0) {
      travelerTypes.push(
        `${children} ${
          children > 1
            ? passengerTypesMap["CNN"].multiple
            : passengerTypesMap["CNN"].single
        }`,
      );
    }
    if (infants > 0) {
      travelerTypes.push(
        `${infants} ${
          infants > 1
            ? passengerTypesMap["INF"].multiple
            : passengerTypesMap["INF"].single
        }`,
      );
    }
    return travelerTypes.join(", ");
  };

  const handleError = () => {
    let nextQuery = params.searchQuery;
    setShowErrorMessage(false);
    setTimeout(() => {
      navigate(`/search-results?${buildQueryString(nextQuery)}`);
    }, backgroundAnimations.delayOut);
  };

  return (
    <Box
      sx={{
        backgroundColor: "white.main",
        height: "100vh",
        width: "100vw",
        position: "fixed",
        zIndex: 101,
      }}
    >
      <>
        {showErrorMessage && (
          <>
            <CtaDialog
              onClose={() => handleError()}
              ctaAction={() => handleError()}
              CTA={CTA}
              title={title}
              subtitle={subtitle}
              text={text}
              icon={
                ResponseIconMap[icon as keyof typeof ResponseIconMap] ||
                undefined
              }
            />
            <MetapartnerTracking
              selectedItinerary={null}
              eventType="pre-conversion"
              createdDate={new Date().toISOString()}
              utm_source={params.searchQuery.utm_source || ""}
              conversion_id=""
              searchQuery={params.searchQuery}
              price={params.searchQuery.totalPrice || 0}
              isDirectBooking={searchForm.isDirectBooking || false}
            />
          </>
        )}
        <CustomAnimation
          in={isFetching || showErrorMessage}
          {...backgroundAnimations}
          unmountOnExit
          mountOnEnter
        >
          <Box
            sx={{
              backgroundColor: "white.main",
              height: "100vh",
              width: "100vw",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <CustomAnimation
              {...titleAnimations}
              in={isFetching || showErrorMessage}
            >
              <Typography
                variant={isTabletUp ? "h1" : "h2"}
                sx={{ color: "primary.main" }}
              >
                {getItineraryCities(params.searchQuery.flights)}
              </Typography>
            </CustomAnimation>
            <CustomAnimation
              in={isFetching || showErrorMessage}
              {...searchFormAnimations}
            >
              <SlotMachineLoader disableModal />
            </CustomAnimation>
            <CustomAnimation
              {...subtitleAnimations}
              in={isFetching || showErrorMessage}
            >
              <Typography
                variant={isTabletUp ? "h4" : "h6"}
                sx={{ color: "primary.main", mt: 4 }}
              >
                {getTripType(params.searchQuery.flights)}
              </Typography>
            </CustomAnimation>
            <CustomAnimation
              {...subtitleAnimations}
              in={isFetching || showErrorMessage}
            >
              <Typography
                variant={isTabletUp ? "h4" : "h6"}
                sx={{ color: "primary.main" }}
              >
                {getTravelDates(params.searchQuery.flights)}
              </Typography>
            </CustomAnimation>
            <CustomAnimation
              {...subtitleAnimations}
              in={isFetching || showErrorMessage}
            >
              <Typography
                variant={isTabletUp ? "h4" : "h6"}
                sx={{ color: "primary.main" }}
              >
                {getTravelerTypes(params.searchQuery.passengers)}
              </Typography>
            </CustomAnimation>
          </Box>
        </CustomAnimation>
      </>
    </Box>
  );
};

export default SearchRedirectPage;
